import apiClient from './apiClient';
import { AxiosResponse } from 'axios';

/**
 * Problem category interface
 */
export interface ProblemCategory {
  id: number;
  tenantId: number;
  name: string;
  description: string | null;
  parentCategoryId: number | null;
  color: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  problemCount?: number;
}

/**
 * Problem tag interface
 */
export interface ProblemTag {
  id: number;
  tenantId: number;
  name: string;
  color: string;
  createdAt: string;
  problemCount?: number;
}

/**
 * Problem option interface for multiple choice
 */
export interface PoolProblemOption {
  id?: number;
  optionText: string;
  isCorrect: boolean;
  optionOrder: number;
}

/**
 * Pool problem interface
 */
export interface PoolProblem {
  id: number;
  tenantId: number;
  sessionId: number | null;
  groupId: number | null;
  problemStatement: string;
  difficultyLevel: number;
  correctSolution: string;
  imagePath: string | null;
  problemType: 'open_ended' | 'multiple_choice';
  categoryId: number | null;
  categoryName?: string;
  categoryColor?: string;
  title: string | null;
  notes: string | null;
  source: string | null;
  isPoolProblem: boolean;
  createdBy: number | null;
  usageCount: number;
  sourceProblemId: number | null;
  displayOrder: number | null;
  createdAt: string;
  updatedAt: string;
  tags?: { id: number; name: string; color: string }[];
  options?: PoolProblemOption[];
}

/**
 * Create pool problem data
 */
export interface CreatePoolProblemData {
  problemStatement: string;
  difficultyLevel?: number;
  correctSolution?: string;
  imagePath?: string;
  problemType?: 'open_ended' | 'multiple_choice';
  categoryId?: number;
  title?: string;
  notes?: string;
  source?: string;
  tagIds?: number[];
  options?: { optionText: string; isCorrect: boolean; optionOrder: number }[];
}

/**
 * Update pool problem data
 */
export interface UpdatePoolProblemData {
  problemStatement?: string;
  difficultyLevel?: number;
  correctSolution?: string;
  imagePath?: string | null;
  problemType?: 'open_ended' | 'multiple_choice';
  categoryId?: number | null;
  title?: string | null;
  notes?: string | null;
  source?: string | null;
  tagIds?: number[];
  options?: { optionText: string; isCorrect: boolean; optionOrder: number }[];
}

/**
 * Pool problem filters
 */
export interface PoolProblemFilters {
  categoryId?: number;
  tagIds?: number[];
  difficultyLevels?: number[];
  problemType?: 'open_ended' | 'multiple_choice';
  search?: string;
  sortBy?: 'created_at' | 'usage_count' | 'difficulty_level' | 'title';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/**
 * Paginated pool problems response
 */
export interface PaginatedPoolProblems {
  problems: PoolProblem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============ Problem Pool API ============

/**
 * Get pool problems with filters and pagination
 */
export const getPoolProblems = async (filters: PoolProblemFilters = {}): Promise<PaginatedPoolProblems> => {
  try {
    const params = new URLSearchParams();

    if (filters.categoryId) params.append('categoryId', String(filters.categoryId));
    if (filters.tagIds?.length) {
      filters.tagIds.forEach(id => params.append('tagIds', String(id)));
    }
    if (filters.difficultyLevels?.length) {
      filters.difficultyLevels.forEach(d => params.append('difficultyLevels', String(d)));
    }
    if (filters.problemType) params.append('problemType', filters.problemType);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const response: AxiosResponse<PaginatedPoolProblems> = await apiClient.get(
      `/api/problem-pool?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching pool problems:', error);
    throw error;
  }
};

/**
 * Get a single pool problem by ID
 */
export const getPoolProblemById = async (id: number): Promise<PoolProblem> => {
  try {
    const response: AxiosResponse<PoolProblem> = await apiClient.get(`/api/problem-pool/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching pool problem ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new pool problem
 */
export const createPoolProblem = async (data: CreatePoolProblemData): Promise<PoolProblem> => {
  try {
    const response: AxiosResponse<PoolProblem> = await apiClient.post('/api/problem-pool', data);
    return response.data;
  } catch (error) {
    console.error('Error creating pool problem:', error);
    throw error;
  }
};

/**
 * Update a pool problem
 */
export const updatePoolProblem = async (id: number, data: UpdatePoolProblemData): Promise<PoolProblem> => {
  try {
    const response: AxiosResponse<PoolProblem> = await apiClient.put(`/api/problem-pool/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating pool problem ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a pool problem
 */
export const deletePoolProblem = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/problem-pool/${id}`);
  } catch (error) {
    console.error(`Error deleting pool problem ${id}:`, error);
    throw error;
  }
};

/**
 * Assign a pool problem to a session/group (clones it)
 */
export const assignPoolProblemToSession = async (
  poolProblemId: number,
  sessionId: number,
  groupId: number,
  displayOrder?: number
): Promise<{ message: string; problem: PoolProblem }> => {
  try {
    const response: AxiosResponse<{ message: string; problem: PoolProblem }> = await apiClient.post(
      `/api/problem-pool/${poolProblemId}/assign`,
      { sessionId, groupId, displayOrder }
    );
    return response.data;
  } catch (error) {
    console.error(`Error assigning pool problem ${poolProblemId} to session:`, error);
    throw error;
  }
};

/**
 * Bulk assign pool problems to a session/group
 */
export const bulkAssignPoolProblems = async (
  problemIds: number[],
  sessionId: number,
  groupId: number
): Promise<{ message: string; problems: PoolProblem[] }> => {
  try {
    const response: AxiosResponse<{ message: string; problems: PoolProblem[] }> = await apiClient.post(
      '/api/problem-pool/bulk-assign',
      { problemIds, sessionId, groupId }
    );
    return response.data;
  } catch (error) {
    console.error('Error bulk assigning pool problems:', error);
    throw error;
  }
};

// ============ Problem Categories API ============

/**
 * Get all problem categories
 */
export const getCategories = async (withCounts = false): Promise<ProblemCategory[]> => {
  try {
    const params = withCounts ? '?withCounts=true' : '';
    const response: AxiosResponse<ProblemCategory[]> = await apiClient.get(`/api/problem-categories${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Get a category by ID
 */
export const getCategoryById = async (id: number): Promise<ProblemCategory> => {
  try {
    const response: AxiosResponse<ProblemCategory> = await apiClient.get(`/api/problem-categories/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching category ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new category
 */
export const createCategory = async (data: {
  name: string;
  description?: string;
  parentCategoryId?: number;
  color?: string;
  displayOrder?: number;
}): Promise<ProblemCategory> => {
  try {
    const response: AxiosResponse<ProblemCategory> = await apiClient.post('/api/problem-categories', data);
    return response.data;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

/**
 * Update a category
 */
export const updateCategory = async (id: number, data: {
  name?: string;
  description?: string;
  parentCategoryId?: number | null;
  color?: string;
  displayOrder?: number;
}): Promise<ProblemCategory> => {
  try {
    const response: AxiosResponse<ProblemCategory> = await apiClient.put(`/api/problem-categories/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating category ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a category
 */
export const deleteCategory = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/problem-categories/${id}`);
  } catch (error) {
    console.error(`Error deleting category ${id}:`, error);
    throw error;
  }
};

// ============ Problem Tags API ============

/**
 * Get all problem tags
 */
export const getTags = async (withCounts = false): Promise<ProblemTag[]> => {
  try {
    const params = withCounts ? '?withCounts=true' : '';
    const response: AxiosResponse<ProblemTag[]> = await apiClient.get(`/api/problem-tags${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tags:', error);
    throw error;
  }
};

/**
 * Get a tag by ID
 */
export const getTagById = async (id: number): Promise<ProblemTag> => {
  try {
    const response: AxiosResponse<ProblemTag> = await apiClient.get(`/api/problem-tags/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching tag ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new tag
 */
export const createTag = async (data: { name: string; color?: string }): Promise<ProblemTag> => {
  try {
    const response: AxiosResponse<ProblemTag> = await apiClient.post('/api/problem-tags', data);
    return response.data;
  } catch (error) {
    console.error('Error creating tag:', error);
    throw error;
  }
};

/**
 * Update a tag
 */
export const updateTag = async (id: number, data: { name?: string; color?: string }): Promise<ProblemTag> => {
  try {
    const response: AxiosResponse<ProblemTag> = await apiClient.put(`/api/problem-tags/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating tag ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a tag
 */
export const deleteTag = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/problem-tags/${id}`);
  } catch (error) {
    console.error(`Error deleting tag ${id}:`, error);
    throw error;
  }
};
